import axios from "axios";
import { useEffect, useState } from "react";
import { API_ORIGIN } from "./apiConfig";
import siteDefaults from "../data/siteDefaults.json";

const USP_BASE_URL = `${API_ORIGIN}/usp`;

export interface ContactSettings {
    name: string;
    addressDisplay: string;
    streetAddress: string;
    locality: string;
    postalCode: string;
    country: string;
    email: string;
    phones: string[];
    officeHours: string[];
    latitude: string;
    longitude: string;
}

export interface SocialSettings {
    facebook: string;
    linkedin: string;
    twitter: string;
}

export interface HeroSettings {
    title: string;
    tagline: string;
    images: string[];
}

export interface SiteSettings {
    contact: ContactSettings;
    social: SocialSettings;
    hero: HeroSettings;
    taxonomy: TaxonomySettings;
    about: AboutSettings;
    home: HomeSettings;
    team: TeamSettings;
    news: NewsSettings;
    careers: CareersSettings;
    privacy: PrivacySettings;
    pageSeo: PageSeoSettings;
}

export interface ThematicArea {
    name: string;
    title: string;
    description: string;
    subAreas: string[];
}

export interface AssignmentType {
    name: string;
    description: string;
    subTypes: string[];
}

export interface ConsultantPosition {
    name: string;
    education: string;
    qualifications: string[];
    responsibilities: string[];
    // Shows thematic area and assignment type fields on the career form
    requiresThematicArea: boolean;
}

export interface TaxonomySettings {
    thematicAreas: ThematicArea[];
    assignmentTypes: AssignmentType[];
    projectAssignmentTypes: string[];
    consultantPositions: ConsultantPosition[];
}

export interface StatItem {
    value: string;
    label: string;
    description: string;
}

export interface AboutSettings {
    foundedYear: string;
    summary: string;
    homeIntro: string[];
    story: string[];
    storyImage: string;
    stats: StatItem[];
}

export interface ImageItem {
    src: string;
    alt?: string;
    name?: string;
}

export interface HomeSettings {
    featuredProjectIds: number[];
    accomplishments: ImageItem[];
    partners: ImageItem[];
}

export interface TeamMember {
    name: string;
    role: string;
    image: string;
    bio: string;
}

export interface TeamSettings {
    members: TeamMember[];
}

export interface NewsArticle {
    slug: string;
    title: string;
    excerpt: string;
    takeaways: string[];
    body: string;
    date: string;
    author: string;
    category: string;
    image: string;
    featured: boolean;
}

export interface NewsSettings {
    articles: NewsArticle[];
}

export interface CareersSettings {
    jobOrder: string[];
}

export interface PrivacySection {
    title: string;
    body: string;
}

export interface PrivacySettings {
    lastUpdated: string;
    sections: PrivacySection[];
}

export interface FaqItem {
    question: string;
    answer: string;
}

export interface PageSeo {
    path: string;
    title: string;
    description: string;
    keywords: string;
    takeaways: string[];
    faqs: FaqItem[];
}

export interface PageSeoSettings {
    pages: PageSeo[];
}

export type SiteSettingKey = keyof SiteSettings;

export const DEFAULT_SITE_SETTINGS: SiteSettings = siteDefaults as unknown as SiteSettings;

const getAuthHeaders = () => {
    const token = localStorage.getItem("admin_access_token");
    return {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
};

// Only allow http(s) and site-relative URLs in href/src
export const safeUrl = (url?: string): string => {
    if (!url) return "";
    const trimmed = url.trim();
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    if (trimmed.startsWith("/") && !trimmed.startsWith("//")) return trimmed;
    return "";
};

export const mergeSettings = (partial?: Partial<Record<SiteSettingKey, any>> | null): SiteSettings => {
    const merged: any = {};
    for (const key of Object.keys(DEFAULT_SITE_SETTINGS) as SiteSettingKey[]) {
        merged[key] = { ...DEFAULT_SITE_SETTINGS[key], ...(partial?.[key] || {}) };
    }
    return merged as SiteSettings;
};

export const toSlug = (text = "") => text.replace(/\s+/g, "-").replace(/[^\w-]/g, "").toLowerCase();

// Keeps a stored value selectable even when it is no longer in the managed list
export const withCurrent = (options: string[], current?: string) =>
    current && !options.includes(current) ? [...options, current] : options;

export const getPageSeo = (settings: SiteSettings, path: string): PageSeo => {
    const found = settings.pageSeo.pages?.find((p) => p.path === path);
    return { path, title: "", description: "", keywords: "", takeaways: [], faqs: [], ...(found || {}) };
};

export const buildFaqSchema = (faqs: FaqItem[]) =>
    faqs.length
        ? {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: faqs.map((f) => ({
                  "@type": "Question",
                  name: f.question,
                  acceptedAnswer: { "@type": "Answer", text: f.answer },
              })),
          }
        : null;

export const getPublicSettings = async (): Promise<SiteSettings> => {
    const response = await axios.get(`${USP_BASE_URL}/settings`);
    return mergeSettings(response.data);
};

export const getAdminSettings = async (): Promise<SiteSettings> => {
    try {
        const response = await axios.get(`${USP_BASE_URL}/admin/settings`, { headers: getAuthHeaders() });
        return mergeSettings(response.data);
    } catch (error: any) {
        throw error.response?.data || { message: "Something went wrong" };
    }
};

export const updateSetting = async <K extends SiteSettingKey>(key: K, value: SiteSettings[K]) => {
    try {
        const response = await axios.put(`${USP_BASE_URL}/admin/settings/${key}`, value, { headers: getAuthHeaders() });
        invalidateSettingsCache();
        return response.data;
    } catch (error: any) {
        throw error.response?.data || { message: "Something went wrong" };
    }
};

export interface PublishStatus {
    configured: boolean;
    lastPublishedAt?: string | null;
    result?: string;
}

export const getPublishStatus = async (): Promise<PublishStatus> => {
    const response = await axios.get(`${USP_BASE_URL}/admin/publish`, { headers: getAuthHeaders() });
    return response.data;
};

export const publishSite = async (): Promise<PublishStatus> => {
    try {
        const response = await axios.post(`${USP_BASE_URL}/admin/publish`, {}, { headers: getAuthHeaders() });
        return response.data;
    } catch (error: any) {
        const result = error.response?.data?.result;
        const messages: Record<string, string> = {
            NOT_CONFIGURED: "Publishing is not configured on the server (USP_AMPLIFY_WEBHOOK_URL).",
            TOO_SOON: "A rebuild was triggered recently. Please wait a couple of minutes.",
            FAILED: "Could not trigger the website rebuild.",
        };
        throw { message: messages[result] || error.response?.data?.message || "Something went wrong" };
    }
};

// Build-time snapshot injected by scripts/prerender.mjs
const readEmbeddedSettings = (): SiteSettings | null => {
    if (typeof document === "undefined") return null;
    const el = document.getElementById("site-settings-data");
    if (!el?.textContent) return null;
    try {
        return mergeSettings(JSON.parse(el.textContent));
    } catch {
        return null;
    }
};

let cachedSettings: SiteSettings | null = readEmbeddedSettings();
let inflight: Promise<SiteSettings> | null = null;

const invalidateSettingsCache = () => {
    inflight = null;
};

const loadSettings = () => {
    if (!inflight) {
        inflight = getPublicSettings()
            .then((s) => (cachedSettings = s))
            .catch(() => cachedSettings || DEFAULT_SITE_SETTINGS);
    }
    return inflight;
};

export const useSiteSettings = (): SiteSettings => {
    const [settings, setSettings] = useState<SiteSettings>(cachedSettings || DEFAULT_SITE_SETTINGS);

    useEffect(() => {
        let active = true;
        loadSettings().then((s) => {
            if (active) setSettings(s);
        });
        return () => {
            active = false;
        };
    }, []);

    return settings;
};
